'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function BodyTypeCalculator() {
  const t = useTranslations("tools.bodyTypeCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  const [inputs, setInputs] = useState({
    bust: '',
    bustUnit: 'inch',
    waist: '',
    waistUnit: 'inch',
    highHip: '',
    highHipUnit: 'inch',
    hip: '',
    hipUnit: 'inch'
  });

  const [results, setResults] = useState<any>(null);

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const convertToInches = (value: number, unit: string) => {
    return unit === 'cm' ? value / 2.54 : value;
  };

  const calculateBodyType = (bust: number, waist: number, highHip: number, hip: number) => {
    const bustWaistDiff = bust - waist;
    const hipWaistDiff = hip - waist;
    const bustHipDiff = bust - hip;
    const hipBustDiff = hip - bust;
    const highHipWaistRatio = highHip / waist;
    const whr = waist / hip;

    let bodyType = '';
    let icon = '👗';
    let descriptionKey = '';

    if (Math.abs(bustHipDiff) <= 1 && hipBustDiff < 3.6 && (bustWaistDiff >= 9 || hipWaistDiff >= 10)) {
      bodyType = 'hourglass';
      icon = '⏳';
      descriptionKey = 'hourglassDesc';
    } else if (hipBustDiff >= 3.6 && hipBustDiff < 10 && hipWaistDiff >= 9 && highHipWaistRatio < 1.193) {
      bodyType = 'bottomHourglass';
      icon = '⏳';
      descriptionKey = 'bottomHourglassDesc';
    } else if (bustHipDiff > 1 && bustHipDiff < 10 && bustWaistDiff >= 9) {
      bodyType = 'topHourglass';
      icon = '⏳';
      descriptionKey = 'topHourglassDesc';
    } else if (hipBustDiff > 2 && hipWaistDiff >= 7 && highHipWaistRatio >= 1.193) {
      bodyType = 'spoon';
      icon = '🥄';
      descriptionKey = 'spoonDesc';
    } else if (hipBustDiff >= 3.6 && hipWaistDiff < 9) {
      bodyType = 'triangle';
      icon = '🍐';
      descriptionKey = 'triangleDesc';
    } else if (bustHipDiff >= 3.6 && bustWaistDiff < 9) {
      bodyType = 'invertedTriangle';
      icon = '🍎';
      descriptionKey = 'invertedTriangleDesc';
    } else {
      bodyType = 'rectangle';
      icon = '🍌';
      descriptionKey = 'rectangleDesc';
    }

    return {
      bodyType,
      icon,
      descriptionKey,
      whr: whr.toFixed(2),
      bustWaistDiff: bustWaistDiff.toFixed(1),
      hipWaistDiff: hipWaistDiff.toFixed(1),
      bustHipDiff: bustHipDiff.toFixed(1)
    };
  };

  const getHealthIndicator = (whr: number) => {
    let healthStatus = '';
    let icon = '⚠️';
    let titleKey = '';
    let messageKey = '';

    if (whr <= 0.80) {
      healthStatus = 'healthy';
      icon = '✅';
      titleKey = 'healthyRange';
      messageKey = 'healthyMessage';
    } else if (whr <= 0.85) {
      healthStatus = 'warning';
      icon = '⚠️';
      titleKey = 'moderateRisk';
      messageKey = 'moderateMessage';
    } else {
      healthStatus = 'warning';
      icon = '⚠️';
      titleKey = 'higherRisk';
      messageKey = 'higherMessage';
    }

    return { healthStatus, icon, titleKey, messageKey };
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    const bustInches = convertToInches(parseFloat(inputs.bust), inputs.bustUnit);
    const waistInches = convertToInches(parseFloat(inputs.waist), inputs.waistUnit);
    const highHipInches = convertToInches(parseFloat(inputs.highHip), inputs.highHipUnit);
    const hipInches = convertToInches(parseFloat(inputs.hip), inputs.hipUnit);

    const result = calculateBodyType(bustInches, waistInches, highHipInches, hipInches);
    const healthInfo = getHealthIndicator(parseFloat(result.whr));

    setResults({
      ...result,
      healthInfo
    });

    setTimeout(() => {
      document.getElementById('results-section')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 100);
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
      borderRadius: '1rem',
      marginBottom: '2rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)'
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    { maxWidth: '100%', margin: '0 auto' },
    {},
    { maxWidth: '700px' },
    { maxWidth: '900px' }
  );

  const toolHeaderStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '1.5rem' },
    {},
    { marginBottom: '2rem' },
    { marginBottom: '2.5rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const calculatorFormStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      marginBottom: '2rem'
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2rem' }
  );

  const formGridStyle = getResponsiveStyle(
    { display: 'grid', gap: '1.5rem', marginBottom: '1.5rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: 'repeat(2, 1fr)' },
    { gridTemplateColumns: 'repeat(2, 1fr)' }
  );

  const formGroupStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column'
  };

  const labelStyle = getResponsiveStyle(
    { fontWeight: 600, color: '#334155', marginBottom: '0.5rem' },
    { fontSize: '0.95rem' },
    { fontSize: '1rem' },
    { fontSize: '1rem' }
  );

  const inputWrapperStyle: React.CSSProperties = {
    display: 'flex',
    gap: '0.5rem',
    alignItems: 'center'
  };

  const inputStyle = getResponsiveStyle(
    {
      flex: 1,
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      transition: 'all 0.3s ease'
    },
    { padding: '0.75rem', fontSize: '1rem' },
    { padding: '0.875rem', fontSize: '1.125rem' },
    { padding: '1rem', fontSize: '1.125rem' }
  );

  const selectStyle = getResponsiveStyle(
    {
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      background: 'white',
      cursor: 'pointer',
      minWidth: '100px'
    },
    { padding: '0.75rem', fontSize: '1rem' },
    { padding: '0.875rem', fontSize: '1.125rem' },
    { padding: '1rem', fontSize: '1.125rem' }
  );

  const calculateButtonStyle = getResponsiveStyle(
    {
      width: '100%',
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'transform 0.2s ease, box-shadow 0.2s ease'
    },
    { padding: '1rem', fontSize: '1.1rem' },
    { padding: '1.125rem', fontSize: '1.125rem' },
    { padding: '1.25rem', fontSize: '1.25rem' }
  );

  const resultsStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2rem' }
  );

  const bodyShapeResultStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #7c3aed 0%, #2563eb 100%)',
      color: 'white',
      borderRadius: '1rem',
      textAlign: 'center' as const,
      marginBottom: '2rem'
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2rem' }
  );

  const shapeIconStyle = getResponsiveStyle(
    { marginBottom: '1rem' },
    { fontSize: '3rem' },
    { fontSize: '3.5rem' },
    { fontSize: '4rem' }
  );

  const shapeNameStyle = getResponsiveStyle(
    { fontWeight: 700, marginBottom: '1rem' },
    { fontSize: '2rem' },
    { fontSize: '2.25rem' },
    { fontSize: '2.5rem' }
  );

  const resultGridStyle = getResponsiveStyle(
    { display: 'grid', gap: '1rem', marginBottom: '2rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: 'repeat(2, 1fr)' },
    { gridTemplateColumns: 'repeat(4, 1fr)' }
  );

  const resultItemStyle: React.CSSProperties = {
    background: '#f8fafc',
    padding: '1.25rem',
    borderRadius: '0.75rem',
    borderLeft: '4px solid #2563eb'
  };

  const resultLabelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: '0.875rem',
    color: '#64748b',
    marginBottom: '0.5rem',
    fontWeight: 500
  };

  const resultValueStyle = getResponsiveStyle(
    { display: 'block', color: colors.textPrimary, fontWeight: 700 },
    { fontSize: '1.25rem' },
    { fontSize: '1.375rem' },
    { fontSize: '1.5rem' }
  );

  const shapeDescriptionStyle = getResponsiveStyle(
    {
      background: '#f0f9ff',
      borderRadius: '0.75rem',
      marginBottom: '1.5rem',
      borderLeft: '4px solid #7c3aed'
    },
    { padding: '1.25rem' },
    { padding: '1.5rem' },
    { padding: '1.5rem' }
  );

  const healthIndicatorStyle = getResponsiveStyle(
    {
      borderRadius: '0.75rem'
    },
    { padding: '1.25rem' },
    { padding: '1.5rem' },
    { padding: '1.5rem' }
  );

  const glassCardStyle = getResponsiveStyle(
    {
      background: colors.glassBg,
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: `1px solid ${colors.glassBorder}`,
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem'
    },
    { padding: '1.5rem' },
    { padding: '2rem', borderRadius: '1.25rem' },
    { padding: '2.5rem', borderRadius: '1.5rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem', lineHeight: 1.7 },
    { fontSize: '1.25rem', lineHeight: 1.8 }
  );

  const listStyle = getResponsiveStyle(
    { paddingLeft: '1.5rem', color: colors.textSecondary, lineHeight: 1.8 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  const measurementGuideStyle = getResponsiveStyle(
    {
      background: '#f8fafc',
      borderRadius: '0.75rem',
      marginTop: '2rem'
    },
    { padding: '1.25rem' },
    { padding: '1.5rem' },
    { padding: '1.5rem' }
  );

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={h1Style}>{t("title")}</h1>
        <p style={summaryTextStyle}>{t("subtitle")}</p>
      </header>

      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={toolHeaderStyle}>
            <h2 style={h2Style}>{t("toolTitle")}</h2>
            <p style={summaryTextStyle}>{t("toolSubtitle")}</p>
          </div>

          <form style={calculatorFormStyle} onSubmit={handleCalculate}>
            <div style={formGridStyle}>
              <div style={formGroupStyle}>
                <label style={labelStyle} htmlFor="bust">{t("bustLabel")}</label>
                <div style={inputWrapperStyle}>
                  <input
                    type="number"
                    id="bust"
                    step="0.1"
                    min="0"
                    placeholder="36"
                    required
                    value={inputs.bust}
                    onChange={(e) => setInputs({...inputs, bust: e.target.value})}
                    style={inputStyle}
                  />
                  <select
                    id="bust-unit"
                    value={inputs.bustUnit}
                    onChange={(e) => setInputs({...inputs, bustUnit: e.target.value})}
                    style={selectStyle}
                  >
                    <option value="inch">{t("inches")}</option>
                    <option value="cm">{t("cm")}</option>
                  </select>
                </div>
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle} htmlFor="waist">{t("waistLabel")}</label>
                <div style={inputWrapperStyle}>
                  <input
                    type="number"
                    id="waist"
                    step="0.1"
                    min="0"
                    placeholder="24"
                    required
                    value={inputs.waist}
                    onChange={(e) => setInputs({...inputs, waist: e.target.value})}
                    style={inputStyle}
                  />
                  <select
                    id="waist-unit"
                    value={inputs.waistUnit}
                    onChange={(e) => setInputs({...inputs, waistUnit: e.target.value})}
                    style={selectStyle}
                  >
                    <option value="inch">{t("inches")}</option>
                    <option value="cm">{t("cm")}</option>
                  </select>
                </div>
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle} htmlFor="highhip">{t("highHipLabel")}</label>
                <div style={inputWrapperStyle}>
                  <input
                    type="number"
                    id="highhip"
                    step="0.1"
                    min="0"
                    placeholder="32"
                    required
                    value={inputs.highHip}
                    onChange={(e) => setInputs({...inputs, highHip: e.target.value})}
                    style={inputStyle}
                  />
                  <select
                    id="highhip-unit"
                    value={inputs.highHipUnit}
                    onChange={(e) => setInputs({...inputs, highHipUnit: e.target.value})}
                    style={selectStyle}
                  >
                    <option value="inch">{t("inches")}</option>
                    <option value="cm">{t("cm")}</option>
                  </select>
                </div>
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle} htmlFor="hip">{t("hipLabel")}</label>
                <div style={inputWrapperStyle}>
                  <input
                    type="number"
                    id="hip"
                    step="0.1"
                    min="0"
                    placeholder="36"
                    required
                    value={inputs.hip}
                    onChange={(e) => setInputs({...inputs, hip: e.target.value})}
                    style={inputStyle}
                  />
                  <select
                    id="hip-unit"
                    value={inputs.hipUnit}
                    onChange={(e) => setInputs({...inputs, hipUnit: e.target.value})}
                    style={selectStyle}
                  >
                    <option value="inch">{t("inches")}</option>
                    <option value="cm">{t("cm")}</option>
                  </select>
                </div>
              </div>
            </div>

            <button
              type="submit"
              style={calculateButtonStyle}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 10px 20px -10px rgba(37, 99, 235, 0.5)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              {t("calculateButton")}
            </button>
          </form>

          {results && (
            <div style={resultsStyle} id="results-section">
              <h3 style={{...h2Style, textAlign: 'center' as const}}>{t("resultsTitle")}</h3>

              <div style={bodyShapeResultStyle}>
                <div style={shapeIconStyle}>{results.icon}</div>
                <h4 style={{fontSize: '1.2rem', marginBottom: '0.5rem', opacity: 0.9}}>{t("yourBodyShape")}</h4>
                <div style={shapeNameStyle}>{t(results.bodyType)}</div>
              </div>

              <div style={resultGridStyle}>
                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t("waistHipRatio")}</span>
                  <span style={resultValueStyle}>{results.whr}</span>
                </div>
                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t("bustWaistDiff")}</span>
                  <span style={resultValueStyle}>{`${results.bustWaistDiff}"`}</span>
                </div>
                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t("hipWaistDiff")}</span>
                  <span style={resultValueStyle}>{`${results.hipWaistDiff}"`}</span>
                </div>
                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t("bustHipDiff")}</span>
                  <span style={resultValueStyle}>{`${results.bustHipDiff}"`}</span>
                </div>
              </div>

              <div style={shapeDescriptionStyle}>
                <h5 style={{color: colors.textPrimary, marginBottom: '0.75rem', fontSize: '1.1rem'}}>{t("aboutYourShape")}</h5>
                <p style={{color: '#475569', lineHeight: 1.6, margin: 0}}>{t(results.descriptionKey)}</p>
              </div>

              <div style={{
                ...healthIndicatorStyle,
                background: results.healthInfo.healthStatus === 'healthy' ? '#d1fae5' : '#fee2e2',
                borderLeft: `4px solid ${results.healthInfo.healthStatus === 'healthy' ? '#10b981' : '#ef4444'}`
              }}>
                <h5 style={{color: colors.textPrimary, marginBottom: '0.75rem', fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                  <span>{results.healthInfo.icon}</span>
                  <span>{t(results.healthInfo.titleKey)}</span>
                </h5>
                <p style={{color: '#475569', lineHeight: 1.6, margin: 0}}>{t(results.healthInfo.messageKey)}</p>
              </div>
            </div>
          )}

          <div style={measurementGuideStyle}>
            <h4 style={{color: colors.textPrimary, marginBottom: '1rem', fontSize: '1.1rem'}}>{t("measurementGuideTitle")}</h4>
            <ul style={{listStyle: 'none', padding: 0, margin: 0}}>
              <li style={{padding: '0.75rem 0', borderBottom: '1px solid #e2e8f0', color: '#475569', lineHeight: 1.6}}>
                <strong style={{color: colors.textPrimary}}>{t("bustLabel")}:</strong> {t("bustGuide")}
              </li>
              <li style={{padding: '0.75rem 0', borderBottom: '1px solid #e2e8f0', color: '#475569', lineHeight: 1.6}}>
                <strong style={{color: colors.textPrimary}}>{t("waistLabel")}:</strong> {t("waistGuide")}
              </li>
              <li style={{padding: '0.75rem 0', borderBottom: '1px solid #e2e8f0', color: '#475569', lineHeight: 1.6}}>
                <strong style={{color: colors.textPrimary}}>{t("highHipLabel")}:</strong> {t("highHipGuide")}
              </li>
              <li style={{padding: '0.75rem 0', color: '#475569', lineHeight: 1.6}}>
                <strong style={{color: colors.textPrimary}}>{t("hipLabel")}:</strong> {t("hipGuide")}
              </li>
            </ul>
          </div>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("introTitle")}</h2>
        <p style={paragraphStyle}>{t("introParagraph1")}</p>
        <p style={paragraphStyle}>{t("introParagraph2")}</p>
        <p style={paragraphStyle}>{t("introParagraph3")}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("howToUseTitle")}</h2>
        <ol style={listStyle}>
          <li style={{marginBottom: '0.75rem'}}>{t("step1")}</li>
          <li style={{marginBottom: '0.75rem'}}>{t("step2")}</li>
          <li style={{marginBottom: '0.75rem'}}>{t("step3")}</li>
          <li style={{marginBottom: '0.75rem'}}>{t("step4")}</li>
          <li>{t("step5")}</li>
        </ol>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("insightsTitle")}</h2>
        <p style={paragraphStyle}>{t("insight1")}</p>
        <p style={paragraphStyle}>{t("insight2")}</p>
        <p style={paragraphStyle}>{t("insight3")}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("bodyTypesTitle")}</h2>

        <div style={{marginBottom: '1.5rem'}}>
          <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem', marginBottom: '0.75rem'}}>⏳ {t("hourglass")}</h3>
          <p style={paragraphStyle}>{t("hourglassDetail")}</p>
        </div>

        <div style={{marginBottom: '1.5rem'}}>
          <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem', marginBottom: '0.75rem'}}>🍐 {t("triangle")}</h3>
          <p style={paragraphStyle}>{t("triangleDetail")}</p>
        </div>

        <div style={{marginBottom: '1.5rem'}}>
          <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem', marginBottom: '0.75rem'}}>🍎 {t("invertedTriangle")}</h3>
          <p style={paragraphStyle}>{t("invertedTriangleDetail")}</p>
        </div>

        <div style={{marginBottom: '1.5rem'}}>
          <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem', marginBottom: '0.75rem'}}>🍌 {t("rectangle")}</h3>
          <p style={paragraphStyle}>{t("rectangleDetail")}</p>
        </div>

        <div>
          <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem', marginBottom: '0.75rem'}}>🥄 {t("spoon")}</h3>
          <p style={paragraphStyle}>{t("spoonDetail")}</p>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("faqTitle")}</h2>

        <div style={{marginBottom: '1.5rem'}}>
          <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem', marginBottom: '0.5rem'}}>{t("faq1Question")}</h3>
          <p style={paragraphStyle}>{t("faq1Answer")}</p>
        </div>

        <div style={{marginBottom: '1.5rem'}}>
          <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem', marginBottom: '0.5rem'}}>{t("faq2Question")}</h3>
          <p style={paragraphStyle}>{t("faq2Answer")}</p>
        </div>

        <div style={{marginBottom: '1.5rem'}}>
          <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem', marginBottom: '0.5rem'}}>{t("faq3Question")}</h3>
          <p style={paragraphStyle}>{t("faq3Answer")}</p>
        </div>

        <div style={{marginBottom: '1.5rem'}}>
          <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem', marginBottom: '0.5rem'}}>{t("faq4Question")}</h3>
          <p style={paragraphStyle}>{t("faq4Answer")}</p>
        </div>

        <div>
          <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem', marginBottom: '0.5rem'}}>{t("faq5Question")}</h3>
          <p style={paragraphStyle}>{t("faq5Answer")}</p>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("referencesTitle")}</h2>
        <ul style={{...listStyle, listStyle: 'decimal'}}>
          <li style={{marginBottom: '0.5rem'}}>
            <a href="https://www.ptpioneer.com/personal-training/tools/body-type-calculator/" target="_blank" rel="noopener noreferrer" style={{color: colors.primary, textDecoration: 'none'}}>
              {t("ref1")}
            </a>
          </li>
          <li style={{marginBottom: '0.5rem'}}>
            <a href="https://www.inchcalculator.com/body-shape-calculator/" target="_blank" rel="noopener noreferrer" style={{color: colors.primary, textDecoration: 'none'}}>
              {t("ref2")}
            </a>
          </li>
          <li style={{marginBottom: '0.5rem'}}>
            <a href="https://thesiliconreview.com/2024/08/discover-your-body-type-the-complete-body-shape-calculator" target="_blank" rel="noopener noreferrer" style={{color: colors.primary, textDecoration: 'none'}}>
              {t("ref3")}
            </a>
          </li>
          <li style={{marginBottom: '0.5rem'}}>
            <a href="https://greenreliefmd.com/body-shape-calculator/" target="_blank" rel="noopener noreferrer" style={{color: colors.primary, textDecoration: 'none'}}>
              {t("ref4")}
            </a>
          </li>
          <li>
            <a href="https://www.calculator.net/body-type-calculator.html" target="_blank" rel="noopener noreferrer" style={{color: colors.primary, textDecoration: 'none'}}>
              {t("ref5")}
            </a>
          </li>
        </ul>
      </section>
    </div>
  );
}